<script setup lang="ts">
import type { Hint } from '@d/app/helper/admin/types'
import type { Storage } from '@d/sdk'
import StorageCard from '@d/app/components/administration/storage/StorageCard.vue'
import { alertQueue } from '@d/form/components/helper/alert'
import UIcon from '@d/form/components/UIcon.vue'
import { tenantApi } from '@d/sdk'
import { mdiCloudPlus, mdiCloudSearch } from '@mdi/js'

const DeleteDialog = defineAsyncComponent(async () => await import('@d/app/components/dialogs/storage/DeleteDialog.vue'))
const EditDialog = defineAsyncComponent(async () => await import('@d/app/components/dialogs/storage/EditDialog.vue'))
const RecoverDialog = defineAsyncComponent(async () => await import('@d/app/components/dialogs/storage/RecoverDialog.vue'))
const BackupDialog = defineAsyncComponent(async () => await import('@d/app/components/dialogs/storage/BackupDialog.vue'))

// Use administation layout
definePageMeta({
  layout: 'administration',
})

const { t } = useI18n({
  useScope: 'local',
})

const storages = ref<Storage[]>([])

async function loadStorages(): Promise<void> {
  const apiStorages = await tenantApi.storages()
  storages.value = apiStorages.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
}

const route = useRoute('client-administration-storage')

function createStorage(): void {
  navigateTo({ name: 'client-administration-storage-link-mode', params: { client: route.params.client, mode: 'add' } })
}

function viewIndex(): void {
  navigateTo({ name: 'client-administration-search', params: { client: route.params.client } })
}

const editDialog = ref(false)
const deleteDialog = ref(false)
const recoverDialog = ref(false)
const backupDialog = ref(false)

const targetStorage = ref<number | undefined>(undefined)

onMounted(async () => {
  await loadStorages()
})

function editStorage(storage: Storage): void {
  targetStorage.value = storage.id
  editDialog.value = true
}

function deleteStorage(storage: Storage): void {
  targetStorage.value = storage.id
  deleteDialog.value = true
}

function recoverStorage(storage: Storage): void {
  targetStorage.value = storage.id
  recoverDialog.value = true
}

function backup(storage: Storage): void {
  targetStorage.value = storage.id
  backupDialog.value = true
}

function submitRenewAuth(storageId: number): void {
  if (!storageId)
    return
  navigateTo({ name: 'client-administration-storage-link-mode', params: { client: route.params.client, mode: 'renew' }, query: { id: storageId } })
}

async function finishDialog(reload?: boolean, hint?: Hint): Promise<void> {
  if (hint) {
    alertQueue.value.push({ text: hint.message, color: hint.color ?? 'primary', timeout: 5000 })
  }
  // Reload storages after finishing dialog
  if (reload)
    await loadStorages()
}
</script>

<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <h1 class="d-flex justify-center ">
          {{ t('title') }}
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <div class="mt-2">
          <div class="d-flex justify-center align-center">
            <v-col cols="11">
              <v-card color="background2" class="pa-2 mb-4 storage-entry create-storage" rounded="xl" @click="createStorage">
                <div class="d-flex py-2" style="align-items: center;">
                  <v-sheet class="ml-2 mr-4 pa-2" color="background2" rounded="pill">
                    <UIcon :icon="mdiCloudPlus" size="32" color="on-background" />
                  </v-sheet>
                  <span class="text-h6">{{ t('createStorage') }}</span>
                </div>
              </v-card>
            </v-col>
            <v-col cols="1">
              <v-card color="background2" class="pa-2 mb-4 storage-entry create-storage" rounded="xl" @click="viewIndex">
                <div class="d-flex py-2 justify-center align-center" style="align-items: center;">
                  <v-sheet color="background2" rounded="pill">
                    <span class="text-h6"><UIcon :icon="mdiCloudSearch" size="32" color="on-background" /></span>
                  </v-sheet>
                </div>
              </v-card>
            </v-col>
          </div>
          <v-divider class="mb-4" />
          <template v-for="item in storages" :key="item.name">
            <StorageCard :item="item" @action:edit="editStorage" @action:delete="deleteStorage" @action:recover="recoverStorage" @action:backup="backup" />
          </template>
        </div>
      </v-col>
    </v-row>
  </v-container>
  <DeleteDialog v-if="deleteDialog && targetStorage" v-model="deleteDialog" width="100%" :storage="targetStorage" @finish="finishDialog" />
  <EditDialog v-if="editDialog && targetStorage" v-model="editDialog" width="100%" :storage="targetStorage" @finish="finishDialog" />
  <RecoverDialog v-if="recoverDialog && targetStorage" v-model="recoverDialog" width="100%" :storage="targetStorage" @finish="finishDialog" @renew="submitRenewAuth" @delete="deleteStorage" />
  <BackupDialog v-if="backupDialog && targetStorage" v-model:show="backupDialog" :storage="targetStorage" />
</template>

<style scoped>
.create-storage {
  cursor: pointer;
}

.margin{
  margin: -24px -8px -24px 0
}
</style>

<i18n lang="yaml">
de:
  title: Storage
  createStorage: Neuen Storage verknüpfen
en:
  title: Storage
  createStorage: Link new Storage
</i18n>
